/**
 * OAuth Callback Page
 *
 * With server-side auth, the actual OAuth callback is handled by
 * /api/auth/callback which exchanges the code and sets cookies.
 *
 * This client-side /callback route shows status while session loads
 * and redirects to the app once authenticated.
 */

import { useEffect, useState, useRef } from "react";
import { useConvexAuth } from "convex/react";
import { useSession } from "@/hooks/use-session";

type CallbackStatus =
  | "waiting_session"
  | "session_authed"
  | "fully_authed"
  | "auth_failed"
  | "timeout";

export function CallbackPage() {
  const [status, setStatus] = useState<CallbackStatus>("waiting_session");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasCheckedError = useRef(false);

  // Session auth state (from server cookies)
  const { isLoading: sessionLoading, isAuthenticated: sessionAuthed } =
    useSession();

  // Convex auth state
  const { isLoading: convexLoading, isAuthenticated: convexAuthed } =
    useConvexAuth();

  // Check for OAuth error in URL (once)
  useEffect(() => {
    if (hasCheckedError.current) return;
    hasCheckedError.current = true;

    const params = new URLSearchParams(window.location.search);
    if (params.has("error")) {
      setErrorMessage(
        params.get("error_description") ||
          params.get("error") ||
          "Unknown error",
      );
      setStatus("auth_failed");
    }
  }, []);

  // Update status based on session state
  useEffect(() => {
    if (!sessionLoading) {
      if (sessionAuthed) {
        setStatus(convexAuthed ? "fully_authed" : "session_authed");
      } else if (status !== "auth_failed" && status !== "timeout") {
        setStatus("auth_failed");
        setErrorMessage(
          "Authentication failed - session not found after callback",
        );
      }
    }
  }, [sessionLoading, sessionAuthed, convexAuthed, status]);

  // Update to fully authed if both are ready
  useEffect(() => {
    if (sessionAuthed && convexAuthed) {
      setStatus("fully_authed");
    }
  }, [convexAuthed, sessionAuthed]);

  // Safety timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (status === "waiting_session") {
        setStatus("timeout");
      }
    }, 15000);

    return () => clearTimeout(timeout);
  }, [status]);

  // Auto-redirect when session authed
  useEffect(() => {
    if (!sessionLoading && sessionAuthed) {
      window.location.replace("/");
    }
  }, [sessionLoading, sessionAuthed]);

  // Handlers
  const handleRetry = () => {
    window.location.href = "/";
  };

  // Status message
  const getStatusMessage = () => {
    switch (status) {
      case "waiting_session":
        return "Loading session...";
      case "session_authed":
        return "Authenticated! Syncing...";
      case "fully_authed":
        return "Authenticated! Redirecting...";
      case "auth_failed":
        return "Authentication failed";
      case "timeout":
        return "Authentication timed out";
      default:
        return "Processing...";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "waiting_session":
        return "#888";
      case "session_authed":
        return "#00ccff";
      case "fully_authed":
        return "#00ff88";
      case "auth_failed":
      case "timeout":
        return "#ff6666";
      default:
        return "#888";
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--background, #0a0a0a)",
        color: "var(--foreground, #e0e0e0)",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* Status */}
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        {/* Spinner */}
        {(status === "waiting_session" || status === "session_authed") && (
          <div
            style={{
              width: 48,
              height: 48,
              border: "3px solid #333",
              borderTopColor: getStatusColor(),
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 24px",
            }}
          />
        )}

        {/* Checkmark */}
        {status === "fully_authed" && (
          <div
            style={{
              width: 48,
              height: 48,
              background: "#00ff88",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              color: "#000",
              fontSize: 24,
            }}
          >
            ✓
          </div>
        )}

        {/* Error icon */}
        {(status === "auth_failed" || status === "timeout") && (
          <div
            style={{
              width: 48,
              height: 48,
              background: "#ff6666",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              color: "#fff",
              fontSize: 24,
            }}
          >
            ✗
          </div>
        )}

        {/* Message */}
        <h2
          style={{
            fontSize: 20,
            fontWeight: 500,
            marginBottom: 8,
            color: getStatusColor(),
          }}
        >
          {getStatusMessage()}
        </h2>

        {/* Error details */}
        {errorMessage && (
          <p style={{ color: "#ff6666", fontSize: 14, marginBottom: 16 }}>
            {errorMessage}
          </p>
        )}

        {/* Actions */}
        <div
          style={{
            marginTop: 24,
            display: "flex",
            gap: 12,
            justifyContent: "center",
          }}
        >
          {(status === "auth_failed" || status === "timeout") && (
            <button
              onClick={handleRetry}
              style={{
                padding: "10px 20px",
                background: "#333",
                border: "none",
                borderRadius: 6,
                color: "#e0e0e0",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
